import { ArrowUpRight } from "lucide-react";
import {
  Radar, Brain, Send, LineChart, ShieldCheck, Zap,
  FileText, Users, CalendarCheck, Linkedin, MessageSquare, Radio, MonitorPlay, Sparkles,
  TrendingUp, Building2, Trophy, HeartHandshake,
} from "lucide-react";
import { Link } from "react-router-dom";
import LondonBackdrop from "@/components/LondonBackdrop";
import CountUp from "@/components/CountUp";

/* ------------------------------------------------------------------ */
/* SLIDE 01 — COVER                                                   */
/* ------------------------------------------------------------------ */
function CoverSlide() {
  return (
    <div className="relative min-h-[calc(100vh-104px)] overflow-hidden flex items-end">
      <LondonBackdrop />
      <div className="relative mx-auto max-w-[1200px] px-6 lg:px-10 pb-24 md:pb-32 w-full">
        <div className="inline-flex items-center gap-2 rounded-full border border-teal/30 bg-teal/[0.08] backdrop-blur px-3 py-1 mb-8">
          <span className="h-1.5 w-1.5 rounded-full bg-teal pulse-dot" />
          <span className="text-[11px] font-mono uppercase tracking-[0.22em] text-teal">
            RevGrow Services (UK) · Executive Deck
          </span>
        </div>
        <h1 className="font-display font-semibold text-5xl md:text-7xl lg:text-[110px] leading-[0.94] tracking-[-0.03em] max-w-5xl">
          AI-Powered <span className="italic font-normal text-teal">Orchestration</span>
          <br />for High-Growth Teams.
        </h1>
        <p className="mt-8 text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">
          One accountable partner. From signal detection to booked meeting.
          Prepared for the modern B2B leader.
        </p>
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl">
          {[
            { v: "46M", l: "Contacts globally" },
            { v: "3.1M", l: "TechBytes subscribers" },
            { v: "96.1%", l: "Data accuracy" },
            { v: "6", l: "Stage framework" },
          ].map((s) => (
            <div key={s.l}>
              <div className="font-display text-3xl md:text-4xl font-semibold">{s.v}</div>
              <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-muted-foreground mt-2">{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* SLIDE 02 — THE PROBLEM                                             */
/* ------------------------------------------------------------------ */
function ProblemSlide() {
  const stats = [
    { v: 73, s: "%", l: "of B2B leads never convert to opportunity" },
    { v: 4.2, s: "x", l: "vendor sprawl in the average GTM stack" },
    { v: 41, s: "%", pfx: "-", l: "drop in reply rates on cold cadences YoY" },
  ];
  return (
    <div className="min-h-[calc(100vh-104px)] flex items-center py-16">
      <div className="mx-auto max-w-[1200px] px-6 lg:px-10 w-full">
        <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-teal mb-4">
          The problem
        </div>
        <h2 className="font-display font-semibold text-4xl md:text-5xl lg:text-[72px] leading-[0.98] tracking-[-0.02em] max-w-4xl">
          B2B pipeline is <span className="italic font-normal text-teal">noise-locked.</span>
        </h2>
        <p className="mt-8 text-base md:text-lg text-muted-foreground max-w-3xl leading-relaxed">
          Buyers are harder to reach. Attribution is broken. Costs are climbing.
          Marketing and sales pass each other in the corridor. The result is a
          growing pile of leads that never close and a shrinking pipeline that
          never grows.
        </p>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-4">
          {stats.map((s) => (
            <div key={s.l} className="rounded-2xl border border-white/[0.08] bg-[#0A121A] p-6 md:p-8">
              <div className="font-display text-5xl md:text-6xl font-semibold text-teal">
                {s.pfx || ""}<CountUp end={s.v} decimals={s.v % 1 !== 0 ? 1 : 0} suffix={s.s} />
              </div>
              <div className="text-sm text-muted-foreground mt-4 leading-relaxed">{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* SLIDE 03 — THE ANSWER                                              */
/* ------------------------------------------------------------------ */
function AnswerSlide() {
  return (
    <div className="min-h-[calc(100vh-104px)] flex items-center py-16">
      <div className="mx-auto max-w-[1200px] px-6 lg:px-10 w-full">
        <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-teal mb-4">
          The RevGrow answer
        </div>
        <h2 className="font-display font-semibold text-4xl md:text-5xl lg:text-[72px] leading-[0.98] tracking-[-0.02em] max-w-5xl">
          One accountable partner. <span className="italic font-normal text-teal">AI-native, end to end.</span>
        </h2>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-2xl border border-white/[0.06] bg-[#0A121A] p-8">
            <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-muted-foreground mb-3">Before RevGrow</div>
            <ul className="space-y-3 text-sm md:text-base">
              {["4+ agencies stitched together", "Attribution is a spreadsheet", "Sales chases 'MQLs' with no intent", "Owned reach ≈ zero, all rented lists"].map((x) => (
                <li key={x} className="flex items-start gap-2 text-muted-foreground">
                  <span className="mt-1 h-1 w-1 rounded-full bg-red-400/70 shrink-0" />
                  {x}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-teal/30 bg-teal/[0.04] p-8">
            <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-teal mb-3">With RevGrow</div>
            <ul className="space-y-3 text-sm md:text-base">
              {[
                "One team from signal to booked meeting",
                "Closed-loop attribution by product & market",
                "BANT-verified, dedup'd, CRM-native handover",
                "TechBytes.org.uk — 3.1M owned subscribers",
              ].map((x) => (
                <li key={x} className="flex items-start gap-2">
                  <span className="mt-1 h-1 w-1 rounded-full bg-teal shrink-0" />
                  <span>{x}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* SLIDE 04 — FRAMEWORK                                               */
/* ------------------------------------------------------------------ */
function FrameworkSlide() {
  const stages = [
    { icon: Radar, n: "01", t: "Signal Detection", d: "15+ live signal types" },
    { icon: Brain, n: "02", t: "Intent Scoring", d: "5 sources → 1 index" },
    { icon: Send, n: "03", t: "Multi-Channel Activation", d: "AI planner adapts spend" },
    { icon: ShieldCheck, n: "04", t: "Real-Time Qualification", d: "18 checks · 4 gates" },
    { icon: Zap, n: "05", t: "Live Handover", d: "Native CRM delivery" },
    { icon: LineChart, n: "06", t: "Continuous Attribution", d: "Weekly ICP re-score" },
  ];
  return (
    <div className="min-h-[calc(100vh-104px)] py-16">
      <div className="mx-auto max-w-[1200px] px-6 lg:px-10 w-full">
        <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-teal mb-4">
          AI-Native Lead Gen Framework
        </div>
        <h2 className="font-display font-semibold text-4xl md:text-5xl lg:text-[64px] leading-[0.98] tracking-[-0.02em] max-w-4xl">
          Six stages. <span className="italic font-normal text-teal">One pipeline.</span>
        </h2>
        <div className="mt-14 grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
          {stages.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.n} className="rounded-2xl border border-white/[0.06] bg-[#0A121A] p-6 relative overflow-hidden">
                <div className="flex items-center justify-between mb-6">
                  <div className="h-10 w-10 rounded-lg bg-teal/10 border border-teal/25 grid place-items-center text-teal">
                    <Icon size={17} />
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">Stage {s.n}</div>
                </div>
                <div className="font-display text-lg font-semibold leading-tight">{s.t}</div>
                <div className="text-xs font-mono uppercase tracking-[0.15em] text-teal mt-3">{s.d}</div>
              </div>
            );
          })}
        </div>
        <div className="mt-8 rounded-2xl border border-teal/25 bg-teal/[0.03] p-6 grid grid-cols-3 gap-4">
          {[["1.8s", "per lead"], ["98%", "accuracy"], ["94.2%", "AI confidence"]].map(([v, l]) => (
            <div key={l}>
              <div className="font-display text-3xl font-semibold">{v}</div>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground mt-1">{l}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* SLIDE 05 — DATA                                                    */
/* ------------------------------------------------------------------ */
function DataSlide() {
  return (
    <div className="min-h-[calc(100vh-104px)] flex items-center py-16">
      <div className="mx-auto max-w-[1200px] px-6 lg:px-10 w-full">
        <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-teal mb-4">
          Data foundation
        </div>
        <h2 className="font-display font-semibold text-4xl md:text-5xl lg:text-[68px] leading-[0.98] tracking-[-0.02em] max-w-4xl">
          <CountUp end={46} suffix="M" /> contacts. <span className="italic font-normal text-teal">Real-time signal.</span>
        </h2>
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 rounded-2xl border border-white/[0.06] bg-[#0A121A] p-8 md:p-10">
            <div className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground mb-6">
              Total Addressable Contacts · Globally
            </div>
            <div className="font-display text-6xl md:text-7xl font-semibold tracking-tighter">~46M</div>
            <div className="mt-10 grid grid-cols-2 gap-4">
              <div className="rounded-xl border border-teal/20 bg-teal/[0.05] p-5">
                <div className="text-xs font-mono uppercase tracking-[0.15em] text-teal mb-2">1st-Party</div>
                <div className="font-display text-4xl font-semibold">4.3M</div>
                <div className="text-xs text-muted-foreground mt-2">Proprietary, verified</div>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
                <div className="text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-2">3rd-Party</div>
                <div className="font-display text-4xl font-semibold">41.7M</div>
                <div className="text-xs text-muted-foreground mt-2">Enriched via partners</div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-5 space-y-4">
            {[
              { v: "96.1%", l: "Verified data accuracy" },
              { v: "45+", l: "Industries reached" },
              { v: "33K+", l: "Tech keywords tracked" },
              { v: "18", l: "Quality checks per lead" },
            ].map((s) => (
              <div key={s.l} className="rounded-2xl border border-white/[0.06] bg-[#0A121A] p-6">
                <div className="font-display text-3xl font-semibold">{s.v}</div>
                <div className="text-xs text-muted-foreground mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* SLIDE 06 — CAPABILITIES                                            */
/* ------------------------------------------------------------------ */
function CapabilitiesSlide() {
  const items = [
    { icon: FileText, t: "Content Syndication", m: "2.4M imp/wk" },
    { icon: Users, t: "SQL & BANT Leads", m: "9.3K/wk" },
    { icon: CalendarCheck, t: "Appointment Setting", m: "68 mtgs/wk" },
    { icon: Linkedin, t: "LinkedIn & Paid Social", m: "5.7% CTR" },
    { icon: MessageSquare, t: "WhatsApp & SMS", m: "95%+ open" },
    { icon: Radio, t: "Webinars & Events", m: "1,412 reg" },
    { icon: MonitorPlay, t: "Programmatic Display", m: "480K clk/mo" },
    { icon: Sparkles, t: "Lead Scoring", m: "1.8s / lead" },
  ];
  return (
    <div className="min-h-[calc(100vh-104px)] py-16">
      <div className="mx-auto max-w-[1200px] px-6 lg:px-10 w-full">
        <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-teal mb-4">
          Core capabilities
        </div>
        <h2 className="font-display font-semibold text-4xl md:text-5xl lg:text-[64px] leading-[0.98] tracking-[-0.02em] max-w-4xl">
          Eight services. <span className="italic font-normal text-teal">One accountable partner.</span>
        </h2>
        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {items.map((c, i) => {
            const Icon = c.icon;
            return (
              <div key={c.t} className="rounded-2xl border border-white/[0.06] bg-[#0A121A] p-5">
                <div className="flex items-center justify-between mb-6">
                  <div className="h-9 w-9 rounded-lg bg-teal/10 border border-teal/25 grid place-items-center text-teal">
                    <Icon size={15} />
                  </div>
                  <div className="text-[10px] font-mono text-muted-foreground">{String(i + 1).padStart(2, "0")}/08</div>
                </div>
                <div className="font-display text-base font-semibold leading-tight">{c.t}</div>
                <div className="text-[11px] font-mono text-teal mt-2">{c.m}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* SLIDE 07 — OWNED MEDIA                                             */
/* ------------------------------------------------------------------ */
function OwnedMediaSlide() {
  return (
    <div className="min-h-[calc(100vh-104px)] flex items-center py-16">
      <div className="mx-auto max-w-[1200px] px-6 lg:px-10 w-full">
        <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-teal mb-4">
          Flagship owned media
        </div>
        <h2 className="font-display font-semibold text-4xl md:text-5xl lg:text-[68px] leading-[0.98] tracking-[-0.02em] max-w-4xl">
          TechBytes<span className="text-teal">.</span>org.uk
        </h2>
        <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">
          The UK tech lead engine. First-party reach, gated premium content and
          event-driven high-intent audiences — all captured under one property.
        </p>
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { v: "3.1M", l: "Subscribers" },
            { v: "22,578", l: "Multi-channel captures" },
            { v: "19,981", l: "Gated premium content" },
            { v: "8,931", l: "Event-driven high-intent" },
          ].map((s) => (
            <div key={s.l} className="rounded-2xl border border-white/[0.06] bg-[#0A121A] p-6">
              <div className="font-display text-4xl font-semibold">{s.v}</div>
              <div className="text-xs text-muted-foreground mt-2">{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* SLIDE 08 — CASE STUDIES                                            */
/* ------------------------------------------------------------------ */
function CaseStudiesSlide() {
  const cases = [
    { c: "Replique", s: "Industrial 3D Printing", outcome: "New-category GTM, decentralised partner net, sales motion across priority verticals." },
    { c: "Outokumpu", s: "Sustainable Stainless Steel", outcome: "Sustainability shifted from message to commercial premium adopted across segments." },
    { c: "Brose", s: "Automotive Mechatronics", outcome: "Coverage model across EV-native OEMs · stronger posture with software platforms." },
    { c: "SBS", s: "Banking & Financial Software", outcome: "Re-anchoring under new brand · qualified pipeline across regions and product lines." },
    { c: "Silverfin", s: "Cloud Accounting Software", outcome: "Trust with economic buyers + shorter evaluation cycles in priority accounts." },
    { c: "Diconium", s: "Digital Transformation", outcome: "Coherent 'Intelligence. Built into everything.' brand platform across all touchpoints." },
  ];
  return (
    <div className="min-h-[calc(100vh-104px)] py-16">
      <div className="mx-auto max-w-[1200px] px-6 lg:px-10 w-full">
        <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-teal mb-4">
          Case studies · outcomes shipped
        </div>
        <h2 className="font-display font-semibold text-4xl md:text-5xl lg:text-[64px] leading-[0.98] tracking-[-0.02em] max-w-4xl">
          Twelve programs. <span className="italic font-normal text-teal">Twelve stories.</span>
        </h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {cases.map((k) => (
            <div key={k.c} className="rounded-2xl border border-white/[0.06] bg-[#0A121A] p-6">
              <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-teal mb-3">{k.s}</div>
              <div className="font-display text-xl font-semibold leading-tight mb-3">{k.c}</div>
              <div className="text-xs text-muted-foreground leading-relaxed">{k.outcome}</div>
            </div>
          ))}
        </div>
        <div className="mt-6 text-[11px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
          + Unispares · SITEC · Comate · Svekon · Jernbro · Proximus NXT
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* SLIDE 09 — WHY REVGROW                                             */
/* ------------------------------------------------------------------ */
function WhyRevGrowSlide() {
  const pillars = [
    { icon: Building2, t: "Built, owned, defensible", d: "TechBytes.org.uk + LeadAudit AI — flagship properties in production." },
    { icon: TrendingUp, t: "Data + reach, not either-or", d: "46M contacts, 96.1% verified accuracy across UK & EMEA." },
    { icon: HeartHandshake, t: "One accountable partner", d: "Awareness to BANT · end-to-end SLAs · no vendor stitching." },
    { icon: Trophy, t: "AI adoption across the value chain", d: "AI embedded at every stage — cycles get smarter, not just launches." },
  ];
  return (
    <div className="min-h-[calc(100vh-104px)] flex items-center py-16">
      <div className="mx-auto max-w-[1200px] px-6 lg:px-10 w-full">
        <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-teal mb-4">
          Why RevGrow
        </div>
        <h2 className="font-display font-semibold text-4xl md:text-5xl lg:text-[64px] leading-[0.98] tracking-[-0.02em] max-w-4xl">
          Demand gen built on signal, <span className="italic font-normal text-teal">not spray-and-pray.</span>
        </h2>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {pillars.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.t} className="rounded-2xl border border-white/[0.06] bg-[#0A121A] p-6">
                <div className="h-10 w-10 rounded-lg bg-teal/10 border border-teal/25 grid place-items-center text-teal mb-6">
                  <Icon size={17} />
                </div>
                <div className="font-display text-lg font-semibold leading-tight mb-3">{p.t}</div>
                <div className="text-xs text-muted-foreground leading-relaxed">{p.d}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* SLIDE 10 — NEXT STEP / CONTACT                                     */
/* ------------------------------------------------------------------ */
function ContactSlide() {
  return (
    <div className="min-h-[calc(100vh-104px)] flex items-center py-16">
      <div className="mx-auto max-w-[1200px] px-6 lg:px-10 w-full">
        <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-teal mb-4">
          Next step
        </div>
        <h2 className="font-display font-semibold text-5xl md:text-6xl lg:text-[92px] leading-[0.98] tracking-[-0.03em] max-w-5xl">
          Let{"\u2019"}s build <span className="italic font-normal text-teal">your pipeline.</span>
        </h2>
        <p className="mt-8 text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">
          A 30-minute working session with a RevGrow strategist — no slides,
          no proposal. Just your pipeline goals, your ICP, and a first plan.
        </p>
        <div className="mt-12 flex flex-wrap gap-3">
          <Link
            to="/#contact"
            className="inline-flex items-center gap-1.5 rounded-full bg-teal text-[#050A0F] px-6 py-3.5 text-[14px] font-medium hover:opacity-90 glow-teal"
          >
            Book a strategy call <ArrowUpRight size={16} />
          </Link>
          <a
            href="mailto:sales@revgrow.co.uk"
            className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-6 py-3.5 text-[14px] hover:border-teal/50 hover:text-teal transition"
          >
            sales@revgrow.co.uk
          </a>
        </div>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 text-sm text-muted-foreground">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-teal mb-2">Registered office</div>
            <div>87 Lozells Street<br />Birmingham B19 2AP<br />United Kingdom</div>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-teal mb-2">Phone</div>
            <div><a href="tel:+441174635393" className="hover:text-teal">+44 117 463 5393</a></div>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-teal mb-2">Owned property</div>
            <div><a href="https://techbytes.org.uk" target="_blank" rel="noopener noreferrer" className="hover:text-teal">TechBytes.org.uk</a></div>
          </div>
        </div>
      </div>
    </div>
  );
}

export const SLIDES = [
  { id: "cover", title: "AI-Powered Orchestration for High-Growth Teams", component: CoverSlide },
  { id: "problem", title: "B2B pipeline is noise-locked", component: ProblemSlide },
  { id: "answer", title: "One accountable partner, AI-native", component: AnswerSlide },
  { id: "framework", title: "AI-Native Lead Gen Framework", component: FrameworkSlide },
  { id: "data", title: "Data foundation · 46M contacts", component: DataSlide },
  { id: "capabilities", title: "Eight capabilities under one roof", component: CapabilitiesSlide },
  { id: "owned-media", title: "TechBytes.org.uk · flagship owned media", component: OwnedMediaSlide },
  { id: "case-studies", title: "Twelve programs, twelve stories", component: CaseStudiesSlide },
  { id: "why-revgrow", title: "Why RevGrow", component: WhyRevGrowSlide },
  { id: "next-step", title: "Book a strategy call", component: ContactSlide },
];
